/**
 * Publikus időnapló megosztás — /public/idonaplo/:token
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface PublicEntry {
  date: string;
  user: string;
  task: string | null;
  durationMinutes: number;
  description?: string;
  hourlyRate?: number;
}

interface PublicData {
  project: { name: string; partner_name?: string; start_date?: string; due_date?: string };
  entries: PublicEntry[];
  summary: { totalMinutes: number; totalHours: number };
  includeHourlyRate: boolean;
}

function fmtDur(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 && m > 0 ? `${h}ó ${m}p` : h > 0 ? `${h}ó` : `${m}p`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
}

function fmtDateFull(iso: string) {
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
}

function initials(name: string) {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#65a30d'];
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const PRINT_CSS = `
@page {
  size: A4;
  margin: 12mm 14mm 16mm;
}
@media print {
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  body, div { background: #fff !important; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 9pt; }

  .screen-only { display: none !important; }
  .print-only  { display: block !important; }

  .print-header {
    border-bottom: 2px solid #0f172a;
    padding: 8px 0 8px;
    margin-bottom: 10px;
  }
  .print-title {
    font-size: 17pt;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
  }
  .print-partner {
    font-size: 9.5pt;
    color: #475569;
    margin-top: 1px;
  }
  .print-meta {
    display: flex !important;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 6px;
    font-size: 8.5pt;
    color: #334155;
  }
  .print-meta-item strong {
    font-weight: 700;
    color: #0f172a;
  }

  .print-user-block {
    margin-top: 10px;
    page-break-inside: avoid;
  }
  .print-user-name {
    font-size: 10pt;
    font-weight: 700;
    color: #0f172a;
    padding: 4px 0 3px;
    border-bottom: 1px solid #cbd5e1;
    margin-bottom: 0;
    display: flex !important;
    justify-content: space-between;
    align-items: baseline;
  }
  .print-user-sub {
    font-size: 8pt;
    color: #64748b;
    font-weight: 400;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0;
    font-size: 8.5pt;
  }
  .print-table th {
    background: #f1f5f9 !important;
    color: #64748b;
    font-size: 7.5pt;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 3px 6px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
  }
  .print-table th.r { text-align: right; }
  .print-table td {
    padding: 4px 6px;
    color: #1e293b;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    line-height: 1.3;
  }
  .print-table td.r { text-align: right; white-space: nowrap; }
  .print-table tr:last-child td { border-bottom: none; }
  .print-table tr:nth-child(even) td { background: #f8fafc !important; }
  .print-table .td-date { color: #64748b; white-space: nowrap; font-size: 7.5pt; }
  .print-table .td-task { font-weight: 500; }
  .print-table .td-desc { font-size: 7.5pt; color: #94a3b8; display: block; margin-top: 0; }
  .print-table .td-dur  { font-weight: 600; color: #334155; }
  .print-table .td-net  { font-weight: 700; color: #0f172a; }

  .print-total-row {
    margin-top: 10px;
    border-top: 1.5px solid #0f172a;
    padding-top: 7px;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
  }
  .print-total-label { font-size: 10pt; font-weight: 700; color: #0f172a; }
  .print-total-sub { font-size: 8pt; color: #64748b; margin-top: 1px; }
  .print-total-amount { font-size: 15pt; font-weight: 800; color: #0f172a; }

  .print-footer {
    position: fixed;
    bottom: 6mm;
    left: 14mm;
    right: 14mm;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #e2e8f0;
    padding-top: 4px;
    font-size: 7.5pt;
    color: #94a3b8;
  }
}
@media screen {
  .print-only { display: none !important; }
}
`;

export default function PublicTimesheetPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`/public/timesheet/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); return; }
        setData(d);
      })
      .catch(() => setError('A megosztás nem elérhető.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots'; meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (loading) return (
    <div style={s.screen}>
      <style>{PRINT_CSS}</style>
      <div style={s.spinnerWrap}>
        <div style={s.spinner} />
        <p style={s.spinnerText}>Betöltés...</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div style={s.screen}>
      <style>{PRINT_CSS}</style>
      <div style={s.errorCard}>
        <div style={s.lockIcon}>🔒</div>
        <h1 style={s.errorTitle}>Nem elérhető</h1>
        <p style={s.errorMsg}>{error || 'A megosztás nem található vagy lejárt.'}</p>
      </div>
    </div>
  );

  // Group by user
  const byUser: Record<string, { entries: PublicEntry[]; totalMin: number; net: number }> = {};
  for (const e of data.entries) {
    if (!byUser[e.user]) byUser[e.user] = { entries: [], totalMin: 0, net: 0 };
    byUser[e.user].entries.push(e);
    byUser[e.user].totalMin += e.durationMinutes;
    if (data.includeHourlyRate && e.hourlyRate) {
      byUser[e.user].net += Math.round((e.durationMinutes / 60) * e.hourlyRate);
    }
  }
  const totalNet = Object.values(byUser).reduce((acc, u) => acc + u.net, 0);
  const userCount = Object.keys(byUser).length;
  const today = new Date().toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={s.page}>
      <style>{PRINT_CSS}</style>

      {/* ══════════════════════════════════════════
          PRINT-ONLY HEADER (hidden on screen)
      ══════════════════════════════════════════ */}
      <div className="print-only">
        <div className="print-header">
          <div className="print-title">{data.project.name}</div>
          {data.project.partner_name && <div className="print-partner">{data.project.partner_name}</div>}
          <div className="print-meta">
            <div className="print-meta-item">Összes idő: <strong>{fmtDur(data.summary.totalMinutes)}</strong></div>
            <div className="print-meta-item">Bejegyzések: <strong>{data.entries.length} db</strong></div>
            {userCount > 1 && <div className="print-meta-item">Munkatársak: <strong>{userCount} fő</strong></div>}
            {data.includeHourlyRate && totalNet > 0 && <div className="print-meta-item">Nettó összesen: <strong>{totalNet.toLocaleString('hu-HU')} Ft</strong></div>}
            {data.project.start_date && <div className="print-meta-item">Időszak: <strong>{fmtDateFull(data.project.start_date)}{data.project.due_date ? ' – ' + fmtDateFull(data.project.due_date) : ''}</strong></div>}
          </div>
        </div>

        {/* Print user blocks */}
        {Object.entries(byUser).map(([user, ud]) => (
          <div key={user} className="print-user-block">
            <div className="print-user-name">
              <span>{user}</span>
              <span className="print-user-sub">
                {fmtDur(ud.totalMin)} · {ud.entries.length} bejegyzés
                {data.includeHourlyRate && ud.net > 0 ? ` · ${ud.net.toLocaleString('hu-HU')} Ft nettó` : ''}
              </span>
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Dátum</th>
                  <th>Feladat / Leírás</th>
                  <th className="r" style={{ width: 65 }}>Idő</th>
                  {data.includeHourlyRate && <th className="r" style={{ width: 90 }}>Nettó</th>}
                </tr>
              </thead>
              <tbody>
                {ud.entries.map((e, i) => {
                  const net = data.includeHourlyRate && e.hourlyRate
                    ? Math.round((e.durationMinutes / 60) * e.hourlyRate) : null;
                  return (
                    <tr key={i}>
                      <td className="td-date">{fmtDate(e.date)}</td>
                      <td>
                        <span className="td-task">{e.task || '—'}</span>
                        {e.description && <span className="td-desc">{e.description}</span>}
                      </td>
                      <td className="r td-dur">{fmtDur(e.durationMinutes)}</td>
                      {data.includeHourlyRate && <td className="r td-net">{net !== null ? net.toLocaleString('hu-HU') + ' Ft' : '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {/* Print total */}
        {data.includeHourlyRate && totalNet > 0 && (
          <div className="print-total-row">
            <div>
              <div className="print-total-label">Nettó összesen</div>
              <div className="print-total-sub">{data.summary.totalHours.toLocaleString('hu-HU', { minimumFractionDigits: 1 })} óra</div>
            </div>
            <div className="print-total-amount">{totalNet.toLocaleString('hu-HU')} Ft</div>
          </div>
        )}

        {/* Print footer */}
        <div className="print-footer">
          <span>Modulum CRM — bizalmas időnapló megosztás</span>
          <span>Nyomtatva: {today}</span>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SCREEN-ONLY CONTENT
      ══════════════════════════════════════════ */}
      <div className="screen-only">
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerInner}>
            <div style={s.headerTop}>
              <span style={s.headerBadge}>Időnapló megosztás</span>
              <button onClick={() => window.print()} style={s.printBtn}>🖨 Nyomtatás</button>
            </div>
            <h1 style={s.projectName}>{data.project.name}</h1>
            {data.project.partner_name && <p style={s.partnerName}>{data.project.partner_name}</p>}
            <div style={s.statsRow}>
              <div style={s.statPill}>
                <span style={s.statValue}>{fmtDur(data.summary.totalMinutes)}</span>
                <span style={s.statLabel}>összes idő</span>
              </div>
              <div style={s.statPill}>
                <span style={s.statValue}>{data.entries.length}</span>
                <span style={s.statLabel}>bejegyzés</span>
              </div>
              {userCount > 1 && (
                <div style={s.statPill}>
                  <span style={s.statValue}>{userCount}</span>
                  <span style={s.statLabel}>munkatárs</span>
                </div>
              )}
              {data.includeHourlyRate && totalNet > 0 && (
                <div style={{ ...s.statPill, ...s.statPillAccent }}>
                  <span style={s.statValue}>{totalNet.toLocaleString('hu-HU')} Ft</span>
                  <span style={s.statLabel}>nettó összesen</span>
                </div>
              )}
            </div>
            {(data.project.start_date || data.project.due_date) && (
              <p style={s.dateRange}>
                {data.project.start_date ? fmtDateFull(data.project.start_date) : '—'}
                {' → '}
                {data.project.due_date ? fmtDateFull(data.project.due_date) : '—'}
              </p>
            )}
          </div>
        </div>

        {/* Cards */}
        <div style={s.content}>
          {data.entries.length === 0 ? (
            <div style={s.emptyCard}>
              <p style={{ fontSize: 32, marginBottom: 12 }}>📋</p>
              <p style={{ color: '#6b7280', fontSize: 14 }}>Még nincs rögzített munka ehhez a projekthez.</p>
            </div>
          ) : (
            Object.entries(byUser).map(([user, ud]) => (
              <div key={user} style={s.userCard}>
                <div style={s.userHeader}>
                  <div style={s.userLeft}>
                    <div style={{ ...s.avatar, background: avatarColor(user) }}>{initials(user)}</div>
                    <div>
                      <div style={s.userName}>{user}</div>
                      <div style={s.userSub}>
                        {fmtDur(ud.totalMin)} · {ud.entries.length} bejegyzés
                        {data.includeHourlyRate && ud.net > 0 && (
                          <> · <strong style={{ color: '#1e3a5f' }}>{ud.net.toLocaleString('hu-HU')} Ft nettó</strong></>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  {ud.entries.map((e, i) => {
                    const net = data.includeHourlyRate && e.hourlyRate
                      ? Math.round((e.durationMinutes / 60) * e.hourlyRate) : null;
                    return (
                      <div key={i} style={{ ...s.entryRow, borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                        <div style={s.dateChip}>{fmtDate(e.date)}</div>
                        <div style={s.entryMain}>
                          <span style={s.entryTask}>{e.task || '—'}</span>
                          {e.description && <span style={s.entryDesc}>{e.description}</span>}
                        </div>
                        <div style={s.entryRight}>
                          <span style={s.entryDur}>{fmtDur(e.durationMinutes)}</span>
                          {net !== null && <span style={s.entryNet}>{net.toLocaleString('hu-HU')} Ft</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {data.includeHourlyRate && totalNet > 0 && (
            <div style={s.totalCard}>
              <div>
                <div style={s.totalLabel}>Nettó összesen</div>
                <div style={s.totalSub}>{data.summary.totalHours.toLocaleString('hu-HU', { minimumFractionDigits: 1 })} óra</div>
              </div>
              <div style={s.totalAmount}>{totalNet.toLocaleString('hu-HU')} Ft</div>
            </div>
          )}

          <div style={s.footer}>
            <span style={{ color: '#94a3b8' }}>Modulum CRM</span>
            <span style={{ color: '#cbd5e1', margin: '0 8px' }}>·</span>
            <span style={{ color: '#94a3b8' }}>Bizalmas időnapló megosztás</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  screen: { minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinnerWrap: { textAlign: 'center' },
  spinner: { width: 32, height: 32, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', margin: '0 auto 12px' },
  spinnerText: { color: '#94a3b8', fontSize: 14 },
  errorCard: { background: '#fff', borderRadius: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e2e8f0', padding: '48px 40px', textAlign: 'center', maxWidth: 360, width: '100%', margin: '0 16px' },
  lockIcon: { fontSize: 40, marginBottom: 16 },
  errorTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8 },
  errorMsg: { fontSize: 14, color: '#64748b', lineHeight: 1.6 },
  header: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  headerInner: { maxWidth: 760, margin: '0 auto', padding: '32px 24px 28px' },
  headerTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  headerBadge: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#93c5fd', background: 'rgba(147,197,253,0.12)', border: '1px solid rgba(147,197,253,0.25)', borderRadius: 99, padding: '4px 12px' },
  printBtn: { fontSize: 12, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' },
  projectName: { fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 4 },
  partnerName: { fontSize: 14, color: '#93c5fd', marginBottom: 20 },
  statsRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  statPill: { background: 'rgba(255,255,255,0.09)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 },
  statPillAccent: { background: 'rgba(59,130,246,0.25)', border: '1px solid rgba(59,130,246,0.4)' },
  statValue: { fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1 },
  statLabel: { fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' },
  dateRange: { fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8 },
  content: { maxWidth: 760, margin: '0 auto', padding: '24px 16px 48px' },
  emptyCard: { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '64px 24px', textAlign: 'center' },
  userCard: { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  userHeader: { padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  userLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0, letterSpacing: '-0.03em' },
  userName: { fontSize: 14, fontWeight: 700, color: '#1e293b' },
  userSub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  entryRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', background: '#fff' },
  dateChip: { fontSize: 11, color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 60, textAlign: 'center' },
  entryMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  entryTask: { fontSize: 13, fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  entryDesc: { fontSize: 11, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  entryRight: { display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 },
  entryDur: { fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 44, textAlign: 'right' },
  entryNet: { fontSize: 13, fontWeight: 700, color: '#1e3a5f', minWidth: 80, textAlign: 'right' },
  totalCard: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', borderRadius: 16, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, boxShadow: '0 4px 12px rgba(15,23,42,0.25)' },
  totalLabel: { fontSize: 14, fontWeight: 700, color: '#fff' },
  totalSub: { fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3 },
  totalAmount: { fontSize: 26, fontWeight: 800, color: '#fff' },
  footer: { textAlign: 'center', fontSize: 12, paddingTop: 8 },
};
